﻿import { Layout, Menu } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  UploadOutlined,
} from '@ant-design/icons';
import {
  ZinAdmin,
  ZinArrowClose,
  ZinAssignments,
  ZinCampaign,
  ZinCandidate,
  ZinClientManagement,
  ZinDashboard,
  ZinEmployerManagement,
  ZinHelp,
  ZinJobs,
  ZinLogoIcon,
  ZinLogoText,
  ZinNetwork,
  ZinOnBoarding,
  ZinReports,
  ZinScheduler,
  ZinSettings,
  ZinSubmission,
  ZinTimesheet,
} from '../../assets/images';

const { Sider } = Layout;

export const SIDEBAR_WIDTH = 200;
export const SIDEBAR_COLLAPSED_WIDTH = 60;

const iconMap = {
  Dashboard: ZinDashboard,
  Jobs: ZinJobs,
  Candidates: ZinCandidate,
  Submissions: ZinSubmission,
  Network: ZinNetwork,
  ClientManagement: ZinClientManagement,
  Clients: ZinClientManagement,
  clients: ZinClientManagement,
  EmployerManagement: ZinEmployerManagement,
  Employers: ZinEmployerManagement,
  employers: ZinEmployerManagement,
  Scheduler: ZinScheduler,
  Admin: ZinAdmin,
  Reports: ZinReports,
  Settings: ZinSettings,
  Help: ZinHelp,
  onboarding: ZinOnBoarding,
  Onboarding: ZinOnBoarding,
  assignments: ZinAssignments,
  Assignments: ZinAssignments,
  assignment: ZinAssignments,
  Assignment: ZinAssignments,
  timesheet: ZinTimesheet,
  Timesheet: ZinTimesheet,
  campaign: ZinCampaign,
  Campaign: ZinCampaign,
  addTestCase: ZinReports,
  viewTestCase: ZinScheduler,
  manageModules: ZinSettings,
  bulkUpload: UploadOutlined,
  'bulk-upload': UploadOutlined,
};

function parseStoredMenu(storageKey) {
  const rawValue = localStorage.getItem(storageKey);

  if (!rawValue || rawValue === 'undefined' || rawValue === 'null' || rawValue.trim() === '') {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    if (Array.isArray(parsedValue)) return parsedValue;
    if (Array.isArray(parsedValue?.menu)) return parsedValue.menu;
    if (Array.isArray(parsedValue?.menus?.menu)) return parsedValue.menus.menu;

    return [];
  } catch (error) {
    console.error(`Error parsing ${storageKey}:`, error);
    return [];
  }
}

function getSelectedKeys(pathname) {
  const cleanPath = pathname.replace(/^\/+/, '');
  if (!cleanPath) return ['dashboard'];

  const parts = cleanPath.split('/');
  return [cleanPath, parts[0], parts.at(-1)].filter(Boolean);
}

function SidebarIcon({ icon, itemKey }) {
  const iconSrc = itemKey === 'bulk-upload'
    ? UploadOutlined
    : itemKey === 'assignments'
      ? ZinAssignments
      : itemKey === 'employers'
        ? ZinEmployerManagement
        : iconMap[icon];

  if (!iconSrc) return null;
  if (typeof iconSrc !== 'string') {
    const Icon = iconSrc;
    return <Icon className="app-sidebar-menu-icon" aria-hidden="true" />;
  }

  return (
    <img
      src={iconSrc}
      alt=""
      className="app-sidebar-menu-icon"
      aria-hidden="true"
    />
  );
}

function buildMenuItems(menuItems) {
  return menuItems.map((item) => ({
    key: item.key,
    icon: <SidebarIcon icon={item.icon} itemKey={item.key} />,
    label: item.label,
    disabled: item.status === 'inactive',
    children: Array.isArray(item.children) ? buildMenuItems(item.children) : undefined,
  }));
}

function withBulkUploadItem(menuItems) {
  const hasTesterMenu = menuItems.some((item) => item.key === 'addTestCase');
  const hasBulkUpload = menuItems.some((item) => item.key === 'bulk-upload');
  if (!hasTesterMenu || hasBulkUpload) return menuItems;

  const nextItems = [...menuItems];
  const manageIndex = nextItems.findIndex((item) => item.key === 'manageModules');
  nextItems.splice(manageIndex >= 0 ? manageIndex + 1 : nextItems.length, 0, {
    key: 'bulk-upload',
    icon: 'bulk-upload',
    label: 'Bulk upload',
    status: 'active',
  });
  return nextItems;
}

function hasClientPermission() {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const client = permissions?.client ?? permissions?.clients;
    if (!client || typeof client !== 'object') return false;
    return ['view', 'add', 'edit'].some((key) => Number(client[key]) === 1 || client[key] === true || client[key] === '1');
  } catch {
    return false;
  }
}

function hasEmployerPermission() {
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const employer = permissions?.employers ?? permissions?.employer;
    if (!employer || typeof employer !== 'object') return false;
    return ['view', 'add', 'edit'].some((key) => Number(employer[key]) === 1 || employer[key] === true || employer[key] === '1');
  } catch {
    return false;
  }
}

function withClientManagementItem(menuItems) {
  const hasClients = menuItems.some((item) => item.key === 'clients' || item.key === 'client');
  if (hasClients || !hasClientPermission()) return menuItems;

  const nextItems = [...menuItems];
  const jobsIndex = nextItems.findIndex((item) => item.key === 'jobs');
  nextItems.splice(jobsIndex >= 0 ? jobsIndex : nextItems.length, 0, {
    key: 'clients',
    icon: 'ClientManagement',
    label: 'Client Management',
    status: 'active',
  });
  return nextItems;
}

function withEmployerManagementItem(menuItems) {
  const hasEmployers = menuItems.some((item) => item.key === 'employers' || item.key === 'employer');
  const hasClients = menuItems.some((item) => item.key === 'clients' || item.key === 'client');
  const hasManagementAccess = hasEmployerPermission() || hasClients || hasClientPermission();
  if (hasEmployers || !hasManagementAccess) return menuItems;

  const nextItems = [...menuItems];
  const jobsIndex = nextItems.findIndex((item) => item.key === 'jobs');
  nextItems.splice(jobsIndex >= 0 ? jobsIndex : nextItems.length, 0, {
    key: 'employers',
    icon: 'EmployerManagement',
    label: 'Employer Management',
    status: 'active',
  });
  return nextItems;
}

export default function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const selectedKeys = useMemo(() => getSelectedKeys(location.pathname), [location.pathname]);

  useEffect(() => {
    if (selectedKeys.length > 0) {
      localStorage.setItem('activeKey', selectedKeys[0]);
    }
  }, [selectedKeys]);

  const items = useMemo(() => {
    const storedMenu = parseStoredMenu('menu');
    return buildMenuItems(withBulkUploadItem(withEmployerManagementItem(withClientManagementItem(storedMenu))));
  }, []);

  function handleToggle() {
    if (!collapsed) {
      setOpenKeys([]);
    }

    onToggle();
  }

  function handleMenuClick({ key }) {
    navigate(`/${key}`);
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={SIDEBAR_WIDTH}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
      className="app-sidebar"
    >
      <div className="app-sidebar-panel">
        <div className="app-sidebar-brand">
          <div className="app-sidebar-logo">
            <img src={ZinLogoIcon} alt="" className="app-sidebar-logo-icon" />
            <img src={ZinLogoText} alt="Zinnext" className="app-sidebar-logo-text" />
          </div>
        </div>

        <button
          type="button"
          className="app-sidebar-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <img src={ZinArrowClose} alt="" aria-hidden="true" />
        </button>

        <div className="app-sidebar-body">
          <Menu
            mode="inline"
            theme="dark"
            selectedKeys={selectedKeys}
            openKeys={collapsed ? [] : openKeys}
            items={items}
            onClick={handleMenuClick}
            onOpenChange={setOpenKeys}
            className="app-sidebar-menu"
          />
        </div>
      </div>
    </Sider>
  );
}
